import { h } from '@/components/dom';
import { button, note, row, section, select, slider } from '@/components/controls';
import { icons } from '@/components/icons';
import { dropzone } from '@/components/dropzone';
import { SAMPLES, sampleThumb, type SampleSource } from '@/image/samples';
import type { App } from './App';

const THUMB = 96;

/** A sample shown small, so the card shows what it will load. */
function sampleCard(app: App, s: SampleSource): HTMLElement {
  const card = h('button', { type: 'button', class: 'sample', title: `${s.label}: ${s.hint}` }, sampleThumb(s, THUMB), h('i', null, s.label));
  card.addEventListener('click', () => void app.loadSample(s.id));
  return card;
}

export function createInputPanel(app: App): HTMLElement {
  const zone = dropzone({
    label: 'Drop a photo here',
    hint: 'or click to browse. PNG, JPEG, WebP, GIF, BMP or SVG. Pasting works too.',
    accept: 'image/*',
    onFiles: (files) => void app.loadImageFile(files[0]),
  });
  const samples = h('div', { class: 'sample-grid' }, ...SAMPLES.map((s) => sampleCard(app, s)));
  const resolution = slider({
    label: 'Resolution',
    min: 64,
    max: 2048,
    step: 32,
    value: app.state.target.resolution,
    format: (v) => `${v} px`,
    hint: 'Longest side of the target the optimizer works on. Higher is slower.',
    onInput: () => {},
    onChange: (v) => void app.setResolution(v),
  });
  const crop = select<'square' | 'fit'>({
    label: 'Framing',
    value: app.state.target.cropMode,
    options: [
      { value: 'square', label: 'Center crop to square' },
      { value: 'fit', label: 'Keep aspect ratio' },
    ],
    onChange: (v) => void app.setResolution(app.state.target.resolution, v),
  });
  const info = h('div', { class: 'info-box' }, 'No source loaded.');
  const projectInput = h('input', { type: 'file', accept: '.json,application/json', style: { display: 'none' } });
  projectInput.addEventListener('change', () => {
    const f = projectInput.files?.[0];
    if (f) void app.openProjectFile(f);
    projectInput.value = '';
  });
  const openProject = button({ label: 'Open project…', icon: icons.folder, onClick: () => projectInput.click() });

  app.store.subscribe((s, p) => {
    if (s.source !== p.source || s.target !== p.target || s === p) {
      resolution.setValue(s.target.resolution);
      crop.setValue(s.target.cropMode);
      if (s.source.kind === 'image') info.textContent = `${s.source.name} at ${s.source.width}×${s.source.height}, target ${s.target.width}×${s.target.height}`;
      else info.textContent = 'No source loaded.';
    }
  }, { immediate: true });

  return h(
    'div',
    null,
    section('Image', [zone, note('Or start from a sample:'), samples, info]),
    section('Target size', [resolution.element, crop.element]),
    section('Project', [row(openProject, projectInput), note('Projects store configuration, pins and the string sequence (not the source pixels).')]),
  );
}
